import SEO from "./SEO";
import SEOConfig from "./SEOConfig";
import truncateAtWord from "../../utils/helpers/truncateAtWord";

const DriverSEO = ({ driver, url }) => {
  if (!driver) return null;

  const fullName = `${driver.firstName} ${driver.lastName}`;

  // The API sends the literal string "null" for an empty bio.
  const bio = driver.bio && driver.bio !== "null" ? driver.bio : null;

  const team = driver.team?.name ? `, команда ${driver.team.name}` : "";
  const description = bio
    ? truncateAtWord(bio)
    : `${fullName} — пілот симрейсинг-ліги Slick Racing Ukraine${team}. Статистика, результати гонок і досягнення в iRacing.`;

  const imageUrl = driver.image || null;
  const { siteUrl, organization } = SEOConfig;
  const fullUrl = url.startsWith("http") ? url : `${siteUrl}${url}`;

  const getAllAchievements = (achievements) => {
    if (!achievements) return [];

    const allAchievements = [];
    // Loop through each simulator category
    Object.keys(achievements).forEach((simulator) => {
      if (Array.isArray(achievements[simulator])) {
        // Add each achievement with the simulator prefix
        achievements[simulator].forEach((achievement) => {
          allAchievements.push({
            ...achievement,
            simulator: simulator,
          });
        });
      }
    });

    return allAchievements;
  };

  // Get all achievements across simulators
  const allAchievements = getAllAchievements(driver.achievements);

  const achievementKeywords = [
    ...new Set(allAchievements.map((achievement) => achievement.name).filter(Boolean)),
  ];

  // Add simulator names to keywords if they have achievements
  const simulatorKeywords = Object.keys(driver.achievements || {}).filter(
    (simulator) =>
      Array.isArray(driver.achievements[simulator]) && driver.achievements[simulator].length > 0,
  );

  // Add relevant keywords for the driver profile
  const keywords = [
    fullName,
    "Slick Racing Ukraine",
    "симрейсинг пілот",
    "сімрейсінг пілот",
    "віртуальний автоспорт",
    "iRacing пілот",
    driver.team?.name || "гонщик",
    "український гонщик",
    "симрейсер",
    "статистика",
    ...simulatorKeywords,
    ...achievementKeywords,
  ].filter(Boolean);

  // Define breadcrumbs for better navigation and SEO
  const breadcrumbs = [
    {
      name: "Головна",
      url: siteUrl,
    },
    {
      name: "Пілоти",
      url: `${siteUrl}/drivers`,
    },
    {
      name: fullName,
      url: fullUrl,
    },
  ];

  // Enhanced structured data for the driver
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: fullName,
    url: fullUrl,
    ...(imageUrl && { image: imageUrl }),
    description: description,
    nationality: { "@type": "Country", name: "Ukraine" },
    jobTitle: "Sim Racing Driver",
    knowsAbout: ["Sim Racing", "iRacing", "Virtual Motorsport"],
    // The league itself, plus the current team when there is one. Teams have no page on the
    // site, so the SportsTeam carries no url.
    memberOf: [
      { "@id": organization.id },
      ...(driver.team?.name
        ? [{ "@type": "SportsTeam", name: driver.team.name, sport: "Sim racing" }]
        : []),
    ],
  };

  // `award` takes plain text in schema.org; there is no Award type.
  if (allAchievements.length > 0) {
    schema.award = allAchievements.map((achievement) =>
      [achievement.name, achievement.at].filter(Boolean).join(" — "),
    );
  }

  return (
    <SEO
      title={`${fullName} — пілот, статистика`}
      description={description}
      keywords={keywords}
      image={imageUrl}
      url={fullUrl}
      schema={schema}
      canonical={fullUrl}
      type="profile"
      breadcrumbs={breadcrumbs}
      lang="uk"
    />
  );
};

export default DriverSEO;
